import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { IBusinessCategory } from '../../../_interfaces/IBusinessCategory';
import ICustomer from '../../../_interfaces/ICustomer';
import { AuthenticationService } from '../../../_services/authentication/authentication.service';
import { CustomersService } from '../../../_services/customers/customers.service';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { AdminStatePanelComponent } from '../../admin-ui/admin-state-panel/admin-state-panel.component';
import { AdminTableActionsComponent } from '../../admin-ui/admin-table-actions/admin-table-actions.component';
import { AdminListToolbarComponent } from '../../admin-ui/admin-list-toolbar/admin-list-toolbar.component';

@Component({
  standalone: true,
  selector: 'app-table-customers',
  templateUrl: './table-customers.component.html',
  styleUrls: ['./table-customers.component.scss'],
  imports: [
    CommonModule,
    TableModule,
    TranslatePipe,
    ButtonModule,
    AdminStatePanelComponent,
    AdminTableActionsComponent,
    AdminListToolbarComponent,
  ],
})
export class TableCustomersComponent implements OnInit {
  customers: ICustomer[] = [];
  managingCustomerId: number | null = null;

  constructor(
    private readonly customersService: CustomersService,
    private readonly authenticationService: AuthenticationService,
    public readonly router: Router,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
    private readonly errorsService: ErrorsService,
  ) {}

  private getTranslatedCategoryName(category: IBusinessCategory): string {
    const code = `${category?.code || ''}`.trim();
    const fallback = `${category?.name || ''}`.trim();
    if (!code) {
      return fallback;
    }

    const key = `BUSINESS_CATEGORIES.${code}`;
    const translated = `${this.translate.instant(key) || ''}`.trim();
    if (!translated || translated === key) {
      return fallback;
    }
    return translated;
  }

  manageCustomer(customer: ICustomer): void {
    if (!customer?.id) return;

    this.managingCustomerId = +customer.id;
    this.authenticationService.startActingCustomer(+customer.id).subscribe({
      next: () => {
        this.managingCustomerId = null;
        this.router.navigate(['/entry']);
      },
      error: (err) => {
        this.managingCustomerId = null;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('COMMON.ERROR'),
          detail: this.errorsService.getMessageFromCustomerError(err),
        });
      },
    });
  }

  confirmDelete(customer: ICustomer): void {
    if (!customer?.id) {
      return;
    }

    this.confirmationService.confirm({
      header: this.translate.instant('ADMIN.CUSTOMERS.CONFIRM.DELETE.TITLE', {
        customer: customer.name,
      }),
      message: this.translate.instant('ADMIN.CUSTOMERS.CONFIRM.DELETE.TEXT', {
        customer: customer.name,
      }),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.delete(customer),
    });
  }

  private delete(customer: ICustomer): void {
    if (!customer?.id) {
      return;
    }
    this.customersService.delete(+customer.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('MESSAGES.CUSTOMERS.DELETE.SUMMARY'),
          detail: this.translate.instant('MESSAGES.CUSTOMERS.DELETE.DETAIL', {
            customer: customer.name,
          }),
        });
        this.load();
      },
      error: (error) => {
        const code = this.errorsService.getMessageFromCustomerError(error);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('MESSAGES.CUSTOMERS.ERROR.SUMMARY'),
          detail: this.translate.instant(`MESSAGES.CUSTOMERS.ERROR.${code}`),
        });
      },
    });
  }

  load(): void {
    this.customersService.getAll().subscribe((customers: ICustomer[]) => {
      this.customers = customers;
    });
  }

  getCategoriesLabel(customer: ICustomer): string {
    const categories = customer?.businessCategories || [];
    if (!categories.length) return '-';
    return categories
      .map((item) =>
        this.getTranslatedCategoryName({
          ...(item as any),
          code: item?.code ?? null,
          name: item?.name || '',
        }),
      )
      .filter(Boolean)
      .join(', ');
  }

  ngOnInit(): void {
    this.load();
  }
}
